<template>
  <div class="add-wrapper">
    <div class="container h-100 bg-white pt-4 h-100 d-flex flex-column justify-content-between">
      <div class="progress-bullets mb-4 d-flex align-items-center justify-content-between">
        <div :class="{done: this.step > 1, active: this.step === 1}" class="bullet"></div>
        <div :class="{done: this.step > 2, active: this.step === 2}" class="bullet"></div>
        <div :class="{done: this.step > 3, active: this.step === 3}" class="bullet"></div>
      </div>
      <div class="form">
        <div v-if="step === 1">
          <p class="lead pb-4">Please enter the details for your referral below, and we will contact them as soon as possible. Thank you for the business!</p>
          <div class="form-label-group mb-3 w-100 mb-3">
            <label for="name">Name*</label>
            <input
              id="name"
              name="name"
              v-model="form.name"
              v-validate="'required'"
              data-vv-validate-on="blur"
              type="text"
              class="form-control">
            <span
              v-show="errors.has('name')"
              class="help text-danger">This field is required</span>
          </div>
          <div class="form-label-group mb-3 w-100 mb-3">
            <label for="phoneNumber">Phone</label>
            <input
              id="phoneNumber"
              name="phoneNumber"
              v-model="form.phoneNumber"
              type="text"
              v-validate="'phoneNumber'"
              data-vv-validate-on="blur"
              class="form-control">
            <span
              v-show="errors.has('phoneNumber')"
              class="help text-danger">This field must be a valid phone number</span>
          </div>
          <div class="form-label-group mb-3 w-100 mb-3">
            <label for="email">Email</label>
            <input
              id="email"
              name="email"
              v-model="form.email"
              v-validate="'email'"
              data-vv-validate-on="blur"
              type="text"
              class="form-control">
            <span
              v-show="errors.has('email')"
              class="help text-danger">This field must be a valid email</span>
         </div>
        </div>
        <div v-if="step === 2">
          <div class="form-label-group mb-3 w-100 mb-3">
            <label for="moveDate">Move Date*</label>
            <Datepicker
              name="proposedMoveDate"
              style="flex: 1"
              ref="arrivingCalendar"
              input-class="form-control"
              v-validate="'required'"
              v-model="form.proposedMoveDate"
              placeholder="Move Date"
              :disabled-dates="disabledDates"
            ></Datepicker>
            <span
              v-show="errors.has('proposedMoveDate')"
              class="help text-danger">This field is required</span>
          </div>
          <div class="form-label-group mb-3 w-100 mb-3">
            <label for="moveSizeId">Move Size*</label>
            <el-select
              name="moveSizeId"
              v-model="form.moveSizeId"
              v-validate="'required'"
              filterable
              placeholder="Select Move Size">
              <el-option
                v-for="item in settings.moveSizes"
                :key="item.id"
                :label="item.name"
                :value="item.id">
              </el-option>
            </el-select>
            <span
              v-show="errors.has('moveSizeId')"
              class="help text-danger">This field is required</span>
          </div>
        </div>
        <div v-if="step === 3">
          <div class="form-label-group mb-3 w-100 mb-3">
            <label for="originAddress">From Address/Zip*</label>
            <input
              id="originAddress"
              name="originAddress"
              v-model="form.originAddress"
              v-validate="'required'"
              data-vv-validate-on="blur"
              type="text"
              class="form-control">
            <span
              v-show="errors.has('originAddress')"
              class="help text-danger">This field is required</span>
          </div>
          <div class="form-label-group mb-3 w-100 mb-3">
            <label for="destinationAddress">To Address/Zip</label>
            <input id="destinationAddress" v-model="form.destinationAddress" type="text" class="form-control">
          </div>
        </div>
        <div v-if="step === 4">
          <div class="finish mb-4">
            <i class="fa fa-check"></i>
          </div>
          <h2 class="text-success text-center font-weight-light my-1">Lead submitted!</h2>
        </div>
      </div>
      <div class="d-flex align-items-center" v-if="this.step < 4">
        <button type="button" class="btn btn-secondary btn-block btn-back my-4 mr-1 py-3" @click="prevStep()">
          <i class="fa fa-arrow-left small"></i> Back
        </button>
        <button type="button"
                class="btn btn-outline-primary btn-block btn-next my-4 ml-1 py-3"
                v-if="this.step !== 3"
                @click="nextStep()">
          Next <i class="fa fa-arrow-right small"></i>
        </button>
        <button type="button"
                class="btn btn-outline-primary btn-block btn-next my-4 ml-1 py-3"
                v-if="this.step === 3"
                @click="submit()"
                :disabled="this.isLoading">
          <template v-if="this.isLoading">
            Please wait...
          </template>
          <template v-else>
            Finish <i class="fa fa-arrow-right small"></i>
          </template>
        </button>
      </div>
      <div class="d-flex align-items-center" v-if="this.step === 4">
        <button type="button" class="btn btn-outline-primary btn-block my-4 mr-1 py-3" @click="$router.push('home')">Show Leads</button>
      </div>
    </div>
  </div>
</template>

<script>
import Datepicker from 'vuejs-datepicker';
import axios from 'axios';
import { DateTime } from 'luxon';
import handleError from '../error-handler';

export default {
  name: 'add-lead',
  components: {
    Datepicker,
  },
  data() {
    return {
      id: '',
      form: {},
      step: 1,
      settings: {},
      isLoading: false,

      disabledDates: {
        to: DateTime.fromJSDate(new Date())
          .plus({ days: -1 })
          .toJSDate(),
      },
    };
  },
  computed: {},
  methods: {
    prevStep() {
      this.step--;

      if (this.step === 0) {
        this.$router.go(-1);
      }
    },
    nextStep() {
      this.$validator.validateAll().then((x) => {
        if (x) {
          this.step++;
        }
      });
    },
    loadSettings() {
      this.settings = window.sm.settings;
    },
    submit() {
      this.$validator.validateAll().then((x) => {
        if (x) {
          this.isLoading = true;

          const form = {
            ...this.form,
          };

          form.proposedMoveDate = DateTime.fromJSDate(form.proposedMoveDate).toFormat('yyyyMMdd');

          axios.post([window.sm.url, `public/affiliates/${this.id}/submit`].join('/'), form)
            .then(() => {
              this.nextStep();
            }).catch((err) => {
              // eslint-disable-next-line
              alert('There was a problem submitting this lead.  Please contact your affiliate sponsor.');
              handleError(err);
            }).finally(() => {
              this.isLoading = false;
            });
        }
      });
    },
  },
  mounted() {
    const { id } = this.$route.params;
    this.id = id;

    this.loadSettings();
  },
};
</script>

<style scoped>
.progress-bullets {
  position: relative;
  width: 80%;
  margin: 0 auto;
}
.progress-bullets:before {
  content: "";
  position: absolute;
  width: 100%;
  height: 1px;
  background: #d8d8d8;
}
.progress-bullets .bullet {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid #d8d8d8;
  z-index: 1;
}
.progress-bullets .bullet.active {
  border-color: #146dc0;
  position: relative;
}
.progress-bullets .bullet.active:before {
  content: "";
  position: absolute;
  width: 12px;
  height: 12px;
  top: 3px;
  left: 3px;
  background: #146dc0;
  border-radius: 50%;
}
.progress-bullets .bullet.done {
  border-color: #146dc0;
  background: #146dc0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}
.progress-bullets .bullet.done:before {
  position: absolute;
  content: "\f00c";
  display: inline-block;
  font-size: 11px !important;
  color: #fff;
  font: normal normal normal 14px/1 FontAwesome;
}

@media (min-width: 768px) {
  .form {
    width: 400px;
    margin-left: auto;
    margin-right: auto;
  }
}
</style>
